import { FALHAS_DE_CONSULTA_TOLERADAS, INTERVALO_DO_JOB_MS, LIMITE_DE_ESPERA_MS } from './scrapeAssincrono';

/**
 * GRAVAR ITENS NO PORTAL (18/10/2026, Licitanet, SAAE Ituiutaba; plano aprovado pelo Heitor). O HUB faz a checagem, o
 * aceite das declarações e grava cada item (valor, marca, modelo), e PARA antes da confirmação final, que fica com o
 * Heitor na tela do portal. A tela tem quatro tempos: a PRÉVIA do que vai em cada item; o botão "Gravar itens no
 * portal"; o resultado item a item do que o PORTAL devolveu; e a instrução de conferir, anexar e confirmar lá, com o
 * "Já enviei" em destaque.
 *
 * As respostas do Heitor (18/10): o HUB grava e para; ME/EPP = sim; marca e modelo DO CADASTRO, com prévia, e o que
 * faltar ele completa na tela.
 *
 * O contrato (Bastião, 18/10/2026):
 * - prévia: `GET /procurement/licitanet/gravar-itens/previa?proposalId=` — montada pelo MESMO código da gravação; linha
 *   com `faltando` não-vazio não é gravada; `ultimaGravacao` traz o último job persistido (recarregar a página não perde
 *   o resultado);
 * - gravação: `POST /procurement/licitanet/gravar-itens` `{ proposalId, marcas? }` → 202 `{ jobId }`; `marcas` é por
 *   `codItem` e sobrepõe o cadastro; consulta em `GET …/gravar-itens/:jobId`;
 * - "Gravar de novo" regrava SÓ os não-ok e os que mudaram; os ok iguais são pulados, e o resultado diz quantos;
 * - recusas antes de gravar: `{codigo, mensagem, message, itens?}`, no 400 do POST e no `recusa` da prévia;
 * - o modo vem do registro dos portais (`etapas.cadastrar.modo === 'grava_itens'`), nunca do nome do portal.
 * ⛔ A gravação NÃO chama a confirmação final nem sobe anexo: isso é do Heitor, no portal.
 */

/** `duplicado`: o backend achou mais de uma linha apontando para o mesmo item do portal (Ituiutaba, 18/10). */
export type CampoQueFalta = 'marca' | 'modelo' | 'preco' | 'codItem' | 'duplicado' | string;

export interface LinhaDaPrevia {
  /**
   * O id DO PORTAL; null se o item nunca foi sincronizado. É a chave das `marcas` do pedido, e só vale como chave se
   * for positivo e único entre as linhas (ver `chaveDaLinha`).
   */
  codItem: number | null;
  numItem: number;
  lote: number | null;
  descricao: string;
  quantidade: number;
  /** JÁ no formato BR que o portal recebe ("6.825,00"). A tela mostra como veio. */
  valorUnitario: string | null;
  total: string | null;
  marca: string | null;
  modelo: string | null;
  origemDaMarca: 'produto' | 'servico' | 'vazio' | string;
  faltando: CampoQueFalta[];
}

export interface RecusaAntesDeGravar {
  codigo: string;
  mensagem?: string | null;
  message?: string | null;
  /** `numItem` dos itens a que a recusa se refere (ITEM_SEM_PRECO, MARCA_MODELO_FALTANDO). */
  itens?: number[];
  /**
   * A recusa some se o pedido trouxer o que falta? Só em MARCA_MODELO_FALTANDO: a tela abre o formulário em vez de só
   * bloquear. ⛔ Preço e codItem faltando recusam SEMPRE ("preço não se improvisa em proposta pública").
   */
  suprivelPeloCorpo?: boolean;
}

export interface ItemGravado {
  codItem: number | null;
  numItem: number;
  lote: number | null;
  /** `pulado` = já gravado IGUAL: deixado como está de propósito (regravar sobrescreveria um ajuste feito no portal). */
  status: 'ok' | 'recusado' | 'pulado' | string;
  valorGravado?: string | null;
  /** A resposta do PORTAL, como veio. */
  mensagem?: string | null;
}

export interface JobDeGravacao {
  jobId: string;
  /** Sucesso: `completed` no job vivo (o `RegistroDeJobs` do backend), `done` na `ultimaGravacao` persistida. */
  status: 'queued' | 'running' | 'done' | 'completed' | 'failed' | 'desconhecido' | string;
  criadoEm?: string;
  concluidoEm?: string;
  /** A frase pronta do backend: "2 itens gravados, 11 pulados: já gravados iguais". */
  resumo?: string | null;
  progresso?: { feitos: number; total: number } | null;
  codigo?: string | null;
  erro?: string | null;
  resultado?: {
    gravados: number; recusados: number; pulados?: number; itens: ItemGravado[];
    /** Qual caminho o job tomou: `criar` = UM POST que cadastrou e enviou a proposta; `editar` = PUT por item. */
    caminho?: 'criar' | 'editar' | string;
    /** ⛔ Só o PORTAL diz: true apenas com o sim dele ao POST de criação. O backend marca a proposta como enviada. */
    enviada?: boolean;
    /**
     * O veredito do portal sobre o POST de criação: UMA resposta para a proposta inteira. O backend a replica em cada
     * item e declara (`vereditoReplicado`); a tela mostra a frase uma vez, não 13.
     */
    envio?: { ok: boolean; status: number; mensagem: string; vereditoReplicado?: boolean } | null;
    /** A flag de envio foi mandada, mas a proposta já existia no portal: o HUB corrigiu por PUT em vez de criar outra. */
    confirmacaoIgnorada?: boolean;
    motivoDaIgnoracao?: string | null;
  } | null;
}

export interface PreviaDaGravacao {
  proposalId: number;
  itemId: number;
  codPregao: string;
  podeGravar: boolean;
  recusa: RecusaAntesDeGravar | null;
  optarMeEpp: boolean;
  resumo: { total: number; vaoGravar: number; recusadas: number };
  linhas: LinhaDaPrevia[];
  ultimaGravacao?: JobDeGravacao | null;
  /**
   * ⭐ O contrato do ENVIO (Bastião, 19/10/2026, decisão do Heitor às 21:05): `true` = a proposta ainda não existe no
   * portal, e gravar quer dizer CADASTRAR e ENVIAR (um POST sem desfazer). A tela troca o botão por "Enviar proposta ao
   * portal", pede confirmação explícita e manda `confirmarEnvio: true`. Campo próprio: a tela não deduz isso do
   * `codProposta` das linhas nem do código de uma recusa (os dois já mudaram de significado uma vez).
   */
  exigeConfirmacao?: boolean;
  propostaExiste?: boolean;
  caminho?: 'criar' | 'editar' | string;
  /** Parte dos itens já cadastrada no portal e parte não: alguém mexeu por fora. O HUB só corrige os cadastrados. */
  estadoMisto?: boolean;
  itensComProposta?: number;
  itensSemProposta?: number;
}

/** As recusas ANTES de gravar: a tela diz qual, em vez da mensagem crua. Código desconhecido mostra a mensagem. */
export const RECUSA_ANTES_DE_GRAVAR: Record<string, string> = {
  PRAZO_ENCERRADO: 'O portal não está mais recebendo proposta: o prazo encerrou.',
  SEM_REPRESENTANTE: 'A conta no portal não tem representante legal cadastrado; sem ele o portal bloqueia o envio.',
  ITEM_SEM_PRECO: 'Há item sem preço na proposta.',
  MARCA_MODELO_FALTANDO: 'Falta marca ou modelo em algum item.',
  // Backend sem o caminho de envio (antes do deploy do envio): a gravação só ATUALIZA proposta que já existe (395, 19/10).
  SEM_COD_PROPOSTA: 'A proposta ainda não existe no portal: ela precisa ser cadastrada e confirmada na tela da Licitanet. Depois disso, grave de novo por aqui.',
  PROPOSTA_NAO_EXISTE_NO_PORTAL: 'A proposta ainda não existe no portal: ela precisa ser cadastrada e confirmada na tela da Licitanet. Depois disso, grave de novo por aqui.',
  // Os do envio (caminho `criar`). CONFIRMACAO_EXIGIDA só chega com prévia velha: a tela relê e o botão vira "Enviar".
  CONFIRMACAO_EXIGIDA: 'A proposta ainda não existe no portal: gravar agora é cadastrar e enviar, e isso pede a sua confirmação. Use "Enviar proposta ao portal".',
  NENHUM_ITEM_PARA_ENVIAR: 'Nenhum item está pronto para enviar (preço, marca/modelo ou vínculo com o portal): um envio assim cadastraria uma proposta vazia.',
  PORTAL_NAO_RESPONDEU: 'O portal não respondeu à leitura do prazo e do representante: sem isso o HUB não grava. Tente de novo em instantes.',
  ENVIO_PERDERIA_ITEM: 'O envio levaria só parte dos itens da proposta; proposta incompleta num órgão público não se conserta depois.',
};

/** A frase da recusa: o rótulo do código, senão a mensagem do backend como veio; com os itens, quando vêm. */
export function fraseDaRecusa(r: RecusaAntesDeGravar): string {
  const base = RECUSA_ANTES_DE_GRAVAR[r.codigo] || r.mensagem || r.message || r.codigo;
  return r.itens?.length ? `${base} Itens: ${r.itens.join(', ')}.` : base;
}

/**
 * O que o Heitor editou na prévia, pela chave da linha (`chaveDaLinha`). Campo ausente = não mexeu (vale o cadastro);
 * string vazia = esvaziou de propósito (e aí volta a faltar).
 */
export type Preenchimento = Record<string, { marca?: string; modelo?: string }>;

const limpo = (v: unknown) => String(v ?? '').trim();

/**
 * A chave da linha no `Preenchimento` e nas `marcas` do pedido: o `codItem`, SE ele identifica uma linha só.
 * ⚠️ codItem 0/null ou repetido entre linhas não é chave: em Ituiutaba (proposta 395, 18/10) a prévia veio com
 * codItem=0 nas 13 linhas, e digitar o modelo de um item mudava o de todos (e o pedido levaria um `marcas[0]` só).
 * Sem chave, a linha não é completável e trava o botão até sincronizar do portal.
 */
export function chaveDaLinha(l: LinhaDaPrevia, linhas: LinhaDaPrevia[]): string | null {
  if (l.codItem == null || !(l.codItem > 0)) return null;
  return linhas.filter((o) => o.codItem === l.codItem).length === 1 ? String(l.codItem) : null;
}

/**
 * Marca/modelo que a linha vai levar: o editado na tela, senão o do cadastro. Só linha com chave válida é editável
 * (sem chave, um campo valeria para várias linhas: Ituiutaba, #543).
 */
export function valorNaTela(l: LinhaDaPrevia, p: Preenchimento, campo: 'marca' | 'modelo', linhas: LinhaDaPrevia[]): string {
  const k = chaveDaLinha(l, linhas);
  const editado = k != null ? p[k]?.[campo] : undefined;
  return editado !== undefined ? editado : String(l[campo] ?? '');
}

/**
 * O que ainda falta numa linha, contando o que foi editado na tela. Na linha com chave válida, marca e modelo valem
 * pelo que está no campo: completar tira a falta, ESVAZIAR um campo do cadastro a põe de volta (o portal exige os
 * dois em todo item). Linha sem chave válida falta `codItem`, mesmo que o backend não tenha dito.
 */
export function faltaEfetiva(l: LinhaDaPrevia, p: Preenchimento, linhas: LinhaDaPrevia[]): CampoQueFalta[] {
  const k = chaveDaLinha(l, linhas);
  if (k == null) {
    const falta = l.faltando || [];
    return falta.includes('codItem') ? falta : ['codItem', ...falta];
  }
  const outras = (l.faltando || []).filter((f) => f !== 'marca' && f !== 'modelo');
  const campos = (['marca', 'modelo'] as const).filter((c) => !limpo(valorNaTela(l, p, c, linhas)));
  return [...outras, ...campos];
}

/**
 * A linha tem campo na tela? Marca e modelo são editáveis em TODA linha com chave válida (pedido do Heitor, 19/10),
 * nascendo com o valor do cadastro; linha de serviço inclusive.
 */
export function linhaEditavel(l: LinhaDaPrevia, linhas: LinhaDaPrevia[]): boolean {
  return chaveDaLinha(l, linhas) != null;
}

/** As frases, na linha, do que a tela não completa. */
export const FRASE_SEM_VINCULO = 'Item sem vínculo com o portal: sincronize do portal antes.';
export const FRASE_DUPLICADO = 'Item repetido: mais de uma linha aponta para o mesmo item do portal.';

/** A chave do React para a linha: lote + número do item, que identificam a linha mesmo sem codItem válido. */
export const chaveDeExibicao = (l: { lote: number | null; numItem: number }) => `${l.lote ?? '-'}/${l.numItem}`;

/**
 * O botão "Gravar itens no portal" (regra combinada com o Bastião): sem recusa, e TODA linha com `faltando` ⊆
 * {marca, modelo} completado na tela. Preço ou codItem faltando travam: a tela não os completa.
 */
export function podeGravarNaTela(previa: PreviaDaGravacao | null | undefined, p: Preenchimento): boolean {
  if (!previa || !previa.linhas.length) return false;
  // A recusa da prévia só não trava quando o corpo pode supri-la (marca/modelo), e aí quem decide são as linhas.
  if (previa.recusa && !previa.recusa.suprivelPeloCorpo) return false;
  // Estado misto: a linha que só falta estar cadastrada no portal não é gravada (o backend a deixa de fora, visível), e não
  // pode travar a correção das que estão.
  return previa.linhas.every((l) => faltaEfetiva(l, p, previa.linhas).every((f) => f === 'codProposta'));
}

/**
 * As `marcas` do pedido: só as linhas em que faltava marca ou modelo, ou cujo valor na tela DIFERE do cadastro; vão
 * os dois campos (o backend sobrepõe o cadastro mesmo onde havia valor). Linha intocada não vai.
 */
export function marcasDoPedido(previa: PreviaDaGravacao, p: Preenchimento): Record<string, { marca: string; modelo: string }> | undefined {
  const out: Record<string, { marca: string; modelo: string }> = {};
  for (const l of previa.linhas) {
    const k = chaveDaLinha(l, previa.linhas);
    if (k == null) continue;
    const marca = limpo(valorNaTela(l, p, 'marca', previa.linhas));
    const modelo = limpo(valorNaTela(l, p, 'modelo', previa.linhas));
    const faltava = (l.faltando || []).some((f) => f === 'marca' || f === 'modelo');
    if (faltava || marca !== limpo(l.marca) || modelo !== limpo(l.modelo)) out[k] = { marca, modelo };
  }
  return Object.keys(out).length ? out : undefined;
}

const ROTULO_DO_QUE_FALTA: Record<string, string> = {
  preco: 'preço', codItem: 'sincronizar do portal', duplicado: 'item repetido', codProposta: 'não cadastrado no portal',
};

/** O resumo do que falta, para a frase ao lado do botão: "item 3 (marca, modelo); item 7 (preco)". */
export function oQueFalta(previa: PreviaDaGravacao, p: Preenchimento): string {
  return previa.linhas
    .map((l) => ({ l, f: faltaEfetiva(l, p, previa.linhas) }))
    .filter((x) => x.f.length)
    .map((x) => `item ${x.l.numItem} (${x.f.map((f) => ROTULO_DO_QUE_FALTA[f] || f).join(', ')})`)
    .join('; ');
}

/** O job acabou? `desconhecido` é terminal, como no scrape: o job saiu da memória do servidor. */
export const jobTerminou = (j: JobDeGravacao | null | undefined) => !!j && ['done', 'completed', 'failed', 'desconhecido'].includes(j.status);

/**
 * Terminou BEM? ⛔ São dois nomes: o GET do job vivo devolve `completed` (o `RegistroDeJobs.concluir`), e a
 * `ultimaGravacao` persistida na proposta diz `done`. Só com `done`, a tela esperava 10 minutos por um job que já tinha
 * acabado e depois dizia "não terminou" (produção, até 19/10).
 */
export const jobConcluido = (j: JobDeGravacao | null | undefined) => !!j && (j.status === 'done' || j.status === 'completed');

/**
 * O placar da gravação contra a prévia, pelos ITENS (o status de cada um), não pelos totais soltos.
 * - `pulado` conta como "no portal": já estava gravado igual e foi deixado como está de propósito;
 * - ⚠️ linha da prévia sem resposta nenhuma não é "gravada": conta à parte, para a tela não dizer "13 de 13" com 12
 *   (é o caso do backend reiniciado no meio: a `ultimaGravacao` fica com o que foi gravado até ali).
 */
export function placarDaGravacao(job: JobDeGravacao | null | undefined, totalDaPrevia: number) {
  const itens = job?.resultado?.itens ?? [];
  const gravados = itens.filter((i) => i.status === 'ok').length;
  const pulados = itens.filter((i) => i.status === 'pulado').length;
  const recusados = itens.filter((i) => i.status !== 'ok' && i.status !== 'pulado').length;
  const semResposta = Math.max(0, totalDaPrevia - itens.length);
  return { gravados, recusados, pulados, semResposta, todos: totalDaPrevia > 0 && gravados + pulados === totalDaPrevia };
}

/** O que `pulado` quer dizer, na tela: sem isso parece falha. */
export const FRASE_DO_PULADO = 'já estava gravado igual: deixado como está, para não sobrescrever um ajuste seu no portal';

/** O valor que o portal gravou difere do da prévia? Compara os dígitos (os dois vêm em formato BR). */
export function valorDivergente(previa: string | null | undefined, gravado: string | null | undefined): boolean {
  if (!gravado) return false;
  const d = (v: string | null | undefined) => String(v ?? '').replace(/\D/g, '').replace(/^0+/, '');
  return d(previa) !== d(gravado);
}

/** A frase do estado misto (parte dos itens cadastrada no portal, parte não). */
export const FRASE_ESTADO_MISTO =
  'Parte dos itens já está cadastrada no portal e parte não: alguém mexeu na proposta por fora. O HUB só corrige os já cadastrados; os outros, cadastre na tela da Licitanet.';
export const FRASE_NAO_CADASTRADO = 'Item não cadastrado no portal: cadastre na tela da Licitanet.';

/** O envio deu certo: o PORTAL aceitou o POST de criação. */
export const propostaEnviadaPeloHub = (j: JobDeGravacao | null | undefined) =>
  j?.resultado?.caminho === 'criar' && j.resultado.enviada === true;

/** O portal recusou o POST de criação: o job terminou (de propósito, não `failed`), com a frase do portal. */
export const envioRecusadoPeloPortal = (j: JobDeGravacao | null | undefined) =>
  j?.resultado?.caminho === 'criar' && j.resultado.enviada !== true;

/** A frase final da confirmação: é ela que separa o "Gravar" (que se corrige) do envio (que não tem desfazer). */
export const FRASE_CONFIRMACAO_FINAL =
  'Esta é a confirmação final do envio no portal: a proposta passa a valer para o órgão, e não há desfazer pelo HUB.';

/** O que a confirmação do envio lista: itens, total, ME/EPP. Total ilegível vira "—", nunca um número que não fecha. */
export function resumoDoEnvio(previa: PreviaDaGravacao): { itens: number; total: string; meEpp: string } {
  const t = totalDaPrevia(previa);
  return {
    itens: previa.linhas.length,
    total: t == null ? '—' : `R$ ${t.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    meEpp: previa.optarMeEpp ? 'sim' : 'não',
  };
}

export const INSTRUCAO_DEPOIS_DE_GRAVAR = 'Confira no portal, suba os anexos, se houver, e confirme o envio lá.';
export const FRASE_GRAVACAO_SEM_CONTATO =
  'Perdi o contato com o servidor enquanto a gravação rodava. Ela segue lá e pode ter terminado: recarregue a proposta para ver.';
export const FRASE_GRAVACAO_SEM_FIM =
  'A gravação não terminou em 10 minutos. Ela segue no servidor: recarregue a proposta daqui a pouco.';

/**
 * Acompanha o job até o fim, no molde do scrape assíncrono (os mesmos intervalo, tolerância a falha de rede e limite).
 * Devolve o job final; perda de contato ou limite viram um job `failed` com a frase, para a tela ter UM caminho.
 */
export async function acompanharGravacao(opts: {
  jobId: string;
  consultar: (jobId: string) => Promise<JobDeGravacao>;
  aoConsultar?: (job: JobDeGravacao) => void;
  dormir?: (ms: number) => Promise<void>;
  agora?: () => number;
}): Promise<JobDeGravacao> {
  const dormir = opts.dormir ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const agora = opts.agora ?? (() => Date.now());
  const ate = agora() + LIMITE_DE_ESPERA_MS;
  let falhasSeguidas = 0;
  while (agora() < ate) {
    await dormir(INTERVALO_DO_JOB_MS);
    let job: JobDeGravacao;
    try {
      job = await opts.consultar(opts.jobId);
      falhasSeguidas = 0;
    } catch {
      falhasSeguidas++;
      if (falhasSeguidas >= FALHAS_DE_CONSULTA_TOLERADAS) return { jobId: opts.jobId, status: 'failed', erro: FRASE_GRAVACAO_SEM_CONTATO };
      continue;
    }
    opts.aoConsultar?.(job);
    if (jobTerminou(job)) return job;
  }
  return { jobId: opts.jobId, status: 'failed', erro: FRASE_GRAVACAO_SEM_FIM };
}

/** "6.825,00" → centavos (inteiro, sem float no meio). null se não for número BR. */
const centavosBr = (v: string | null | undefined): number | null => {
  const m = /^(\d{1,3}(?:\.\d{3})*|\d+),(\d{2})$/.exec(String(v ?? '').trim());
  return m ? Number(m[1].replace(/\./g, '')) * 100 + Number(m[2]) : null;
};

/**
 * O total da proposta que vai ao portal: a soma do `total` das linhas da prévia (o que o backend manda, já em BR).
 * null se alguma linha não tiver total legível: a confirmação do envio não mostra um número que não fecha.
 */
export function totalDaPrevia(previa: PreviaDaGravacao): number | null {
  let soma = 0;
  for (const l of previa.linhas) {
    const c = centavosBr(l.total);
    if (c == null) return null;
    soma += c;
  }
  return soma / 100;
}
